package core.firebase

import kotlin.js.Promise

// Firebase Auth External Declarations
external interface FirebaseAuth {
    val currentUser: FirebaseUser?
    fun signInWithPopup(provider: GoogleAuthProvider): Promise<UserCredential>
    fun signOut(): Promise<Unit>
    fun onAuthStateChanged(callback: (FirebaseUser?) -> Unit): () -> Unit
}

external interface FirebaseUser {
    val uid: String
    val email: String?
    val displayName: String?
    val photoURL: String?
}

external interface UserCredential {
    val user: FirebaseUser
}

external interface GoogleAuthProvider

// Firestore External Declarations
external interface Firestore {
    fun collection(path: String): CollectionReference
    fun doc(path: String): DocumentReference
    fun batch(): WriteBatch
}

external interface CollectionReference {
    fun doc(id: String? = definedExternally): DocumentReference
    fun add(data: Any): Promise<DocumentReference>
    fun get(): Promise<QuerySnapshot>
    fun where(field: String, op: String, value: Any): Query
}

external interface DocumentReference {
    val id: String
    fun set(data: Any, options: Any? = definedExternally): Promise<Unit>
    fun update(data: Any): Promise<Unit>
    fun delete(): Promise<Unit>
    fun get(): Promise<DocumentSnapshot>
    fun collection(path: String): CollectionReference
}

external interface DocumentSnapshot {
    val id: String
    val exists: Boolean
    fun data(): Any?
}

external interface QuerySnapshot {
    val docs: Array<DocumentSnapshot>
    val size: Int
    fun forEach(callback: (DocumentSnapshot) -> Unit)
}

external interface Query {
    fun get(): Promise<QuerySnapshot>
    fun where(field: String, op: String, value: Any): Query
}

external interface WriteBatch {
    fun set(ref: DocumentReference, data: Any): WriteBatch
    fun update(ref: DocumentReference, data: Any): WriteBatch
    fun delete(ref: DocumentReference): WriteBatch
    fun commit(): Promise<Unit>
}

// Firebase Service Object
object FirebaseService {
    val auth: FirebaseAuth
        get() = js("window.firebaseAuth") as FirebaseAuth
    
    val db: Firestore
        get() = js("window.firebaseDb") as Firestore
    
    val googleProvider: GoogleAuthProvider
        get() = js("window.googleProvider") as GoogleAuthProvider
    
    val currentUser: FirebaseUser?
        get() = auth.currentUser
    
    fun signInWithGoogle(): Promise<UserCredential> {
        return auth.signInWithPopup(googleProvider)
    }
    
    fun signOut(): Promise<Unit> {
        return auth.signOut()
    }
    
    fun onAuthStateChanged(callback: (FirebaseUser?) -> Unit): () -> Unit {
        return auth.onAuthStateChanged(callback)
    }
}
